/**
 * useGroupsData Hook
 * 
 * Real-time listener for discovery_groups collection
 * Provides CRUD operations for scrape groups
 */

import { useState, useEffect } from "react";
import {
  collection,
  query,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../auth/AuthContext";

export const useGroupsData = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  // Real-time listener
  useEffect(() => {
    if (!user) {
      setGroups([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const q = query(
      collection(db, "discovery_groups"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const groupsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setGroups(groupsData);
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching groups:", err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // Create new group
  const createGroup = async (groupData) => {
    try {
      const docRef = await addDoc(collection(db, "discovery_groups"), {
        name: groupData.name,
        target: groupData.target || 0,
        targetType: groupData.targetType || "total", // one of TARGET_TYPES (utils/groupTargets.js)
        color: groupData.color || "#4f46e5",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: user?.uid || "unknown",
      });
      return { success: true, id: docRef.id };
    } catch (err) {
      console.error("Error creating group:", err);
      return { success: false, error: err.message };
    }
  };

  // Update existing group
  const updateGroup = async (groupId, updates) => {
    try {
      const groupRef = doc(db, "discovery_groups", groupId);
      await updateDoc(groupRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (err) {
      console.error("Error updating group:", err);
      return { success: false, error: err.message };
    }
  };

  // Delete group
  const deleteGroup = async (groupId) => {
    try {
      await deleteDoc(doc(db, "discovery_groups", groupId));
      return { success: true };
    } catch (err) {
      console.error("Error deleting group:", err);
      return { success: false, error: err.message };
    }
  };

  return {
    groups,
    loading,
    error,
    createGroup,
    updateGroup,
    deleteGroup,
  };
};
